import { Component, Input, Output, EventEmitter, OnDestroy, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Attachment } from '@shared/entities/attachment';
import { ThemeService } from '../services/theme.service';
import { FsAttachmentService } from '@shared/services/api/fs-attachment.service';
import { catchError, takeUntil } from 'rxjs/operators';
import { of, Subject } from 'rxjs';

@Component({
  selector: 'app-file-input-history',
  templateUrl: './file-input-history.component.html',
  styleUrl: './file-input-history.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileInputHistoryComponent),
      multi: true
    }
  ]
})
export class FileInputHistoryComponent implements OnDestroy, ControlValueAccessor {
  @Input() titleKey = 'FILE_INPUT_HISTORY.DEFUALT_TITLE';
  @Input() uploadLabelKey = 'FILE_INPUT_HISTORY.DEFAULT_UPLOAD';
  @Input() noFilesKey = 'FILE_INPUT_HISTORY.NO_FILES_UPLOADED';
  @Input() accept = '.pdf,.doc,.docx';
  @Input() infoPopover?: string;
  @Input() attachments: Attachment[] | string[] = [];
  @Input() filterByAccept = false;
  @Input() hideUploadLabel = false;

  @Input() downloadProperty = 'id';

  @Input() onUpload?: (file: File) => void;
  @Input() onRemove?: (attachmentId: string) => void;

  @Input() isUploading = false;
  @Input() uploadError?: string | null;
  @Input() removingIds?: Set<string>;
  @Input() removeError?: string | null;

  @Output() fileUploaded = new EventEmitter<File>();
  @Output() fileDeleted = new EventEmitter<string>();

  fileControl = new FormControl<File | null>(null);
  downloadingIds = new Set<string>();
  private destroy$ = new Subject<void>();

  private onChange: (v: File | null) => void = () => {
    // Implementation
  };
  private onTouched: () => void = () => {
    // Implementation
  };
  disabled = false;

  constructor(
    private _themeService: ThemeService,
    private _fsAttachmentService: FsAttachmentService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFileSelected(file: File | File[] | null) {
    if (file && !Array.isArray(file)) {
      if (this.onUpload) {
        this.onUpload(file);
      } else {
        this.onChange(file);
        this.onTouched();
      }

      this.fileUploaded.emit(file);
    }
  }

  onFileRemoved() {
    if (!this.onUpload) {
      this.onChange(null);
      this.fileControl.setValue(null);
    }
  }

  downloadDocument(attachmentId: string, fileName?: string) {
    if (!attachmentId || this.downloadingIds.has(attachmentId)) {
      return;
    }

    this.downloadingIds.add(attachmentId);

    this._fsAttachmentService
      .downloadFile(attachmentId, fileName)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.downloadingIds.delete(attachmentId);
          return of(null);
        })
      )
      .subscribe(() => {
        this.downloadingIds.delete(attachmentId);
      });
  }

  deleteDocument(attachmentId: string) {
    const index = this.attachments.findIndex((att) => {
      const attId = this.getAttachmentId(att);
      return attId === attachmentId;
    });

    if (index > -1) {
      this.attachments.splice(index, 1);
    }

    if (this.onRemove) {
      this.onRemove(attachmentId);
    } else {
      if (this.attachments.length === 0) {
        this.onChange(null);
        this.fileControl.setValue(null);
      }
    }

    this.fileDeleted.emit(attachmentId);
  }

  isAttachmentArray(): boolean {
    if (this.attachments.length === 0) return false;
    return typeof this.attachments[0] !== 'string';
  }

  normalizeAttachments(): Attachment[] {
    if (this.attachments.length === 0) {
      return [];
    }

    if (typeof this.attachments[0] === 'string') {
      return (this.attachments as string[]).map((path) => {
        const fileName = path.split('/').pop() || path;
        const attachment = new Attachment();
        attachment.id = path;
        attachment.filePath = path;
        attachment.fileName = fileName;
        return attachment;
      });
    }

    return this.attachments as Attachment[];
  }

  isCurrentlyRemoving(attachmentId: string): boolean {
    return this.removingIds?.has(attachmentId) || false;
  }

  hasRemovingItems(): boolean {
    return this.removingIds ? this.removingIds.size > 0 : false;
  }

  isCurrentlyDownloading(attachmentId: string): boolean {
    return this.downloadingIds.has(attachmentId);
  }

  getAttachmentId(attachment: any): string | undefined {
    if (!attachment) return undefined;
    if (typeof attachment === 'string') return attachment;
    const prop = this.downloadProperty || 'id';
    return attachment[prop] ?? attachment.id;
  }

  writeValue(obj: any): void {
    if (obj instanceof File) {
      this.fileControl.setValue(obj);
    } else {
      this.fileControl.setValue(null);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.fileControl.disable();
    } else {
      this.fileControl.enable();
    }
  }

  get isLight(): boolean {
    return this._themeService.isLight();
  }

  get filteredAttachments(): Attachment[] {
    const normalized = this.normalizeAttachments();

    if (!this.filterByAccept) {
      return normalized;
    }

    return normalized.filter((attachment) => {
      if (!attachment.contentType) {
        return false;
      }
      return this.matchesAcceptedType(attachment.contentType);
    });
  }

  private matchesAcceptedType(contentType: string): boolean {
    const acceptTypes = this.accept.split(',').map((t) => t.trim());

    for (const acceptType of acceptTypes) {
      if (acceptType.startsWith('.')) {
        const extension = acceptType.substring(1);
        if (contentType.includes(extension)) {
          return true;
        }
      } else if (acceptType.endsWith('/*')) {
        const typePrefix = acceptType.substring(0, acceptType.length - 2);
        if (contentType.startsWith(typePrefix + '/')) {
          return true;
        }
      } else if (contentType === acceptType) {
        return true;
      }
    }

    return false;
  }
}
